/**
 * Safe `return_to` query-param handling for post-auth redirects.
 *
 * Phase 2 (Plan-ProdagiAIAgent-AdminHC.md §4 piece 1): the admin Help Center
 * is gated server-side by customer-front auth. Storm's `_require_customer_session`
 * redirects unauth visitors to `{FRONTEND_URL}/login?return_to=<encoded URL>`.
 * Login.tsx / Register.tsx must honor that param after successful auth —
 * without this, the redirect loop breaks (user lands on /dashboard instead
 * of the protected admin-HC page they were trying to reach).
 *
 * Open-redirect safety: only honor `return_to` URLs that are
 *   - Same-origin (relative or absolute matching window.location.origin), OR
 *   - On the `prodagi.ai` OR `askprodigy.ai` apex or any subdomain (HTTPS
 *     only in prod) — both domains are live and both are allowed, OR
 *   - On localhost (any port) for local dev.
 *
 * Anything else falls through to the supplied default. Never `eval` /
 * `decodeURI` past the URL parser — `new URL()` is the validation gate.
 */

const FALLBACK = '/dashboard';

/**
 * Validate that `candidate` is safe to redirect to. Returns the original
 * string if safe, `null` if not.
 */
function validateReturnTo(candidate: string | null): string | null {
  if (!candidate) return null;

  // Relative path on this origin — always safe.
  if (candidate.startsWith('/') && !candidate.startsWith('//')) {
    return candidate;
  }

  let url: URL;
  try {
    // For absolute URLs, the second arg is ignored. For protocol-relative
    // (`//evil.com`) URLs, `new URL` resolves against the base which would
    // make them absolute on the attacker domain — caught by the host check
    // below.
    url = new URL(candidate, window.location.origin);
  } catch {
    return null;
  }

  // Same-origin always allowed.
  if (url.origin === window.location.origin) {
    return url.pathname + url.search + url.hash;
  }

  // Allow the brand apex + any subdomain (HTTPS only). BOTH domains are
  // live and BOTH must stay allowed: askprodigy.ai is the new one, and
  // prodagi.ai is being RETAINED, not decommissioned. Dropping either
  // breaks the post-login return_to redirect from that domain's pages.
  const BRAND_APEXES = ['prodagi.ai', 'askprodigy.ai'];
  if (
    url.protocol === 'https:'
    && BRAND_APEXES.some(
      (apex) => url.hostname === apex || url.hostname.endsWith(`.${apex}`),
    )
  ) {
    return url.toString();
  }

  // Allow localhost (any port, any protocol) for local dev.
  if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
    return url.toString();
  }

  return null;
}

/**
 * Read the `return_to` query parameter from the current URL and return a
 * safe redirect target. Falls through to `defaultPath` (default `/dashboard`)
 * when the param is absent or fails the open-redirect check.
 */
export function getSafeReturnTo(defaultPath: string = FALLBACK): string {
  if (typeof window === 'undefined') return defaultPath;
  const params = new URLSearchParams(window.location.search);
  const candidate = params.get('return_to');
  const safe = validateReturnTo(candidate);
  return safe ?? defaultPath;
}

/**
 * After a successful auth, navigate to the safe `return_to` if present,
 * otherwise to `defaultPath`. Uses `window.location.href` for absolute URLs
 * (e.g. `https://help.prodagi.ai/...`) and `react-router` `navigate()` for
 * relative paths (avoids a full page reload on in-app destinations).
 */
export function redirectAfterAuth(
  navigate: (path: string) => void,
  defaultPath: string = FALLBACK,
): void {
  const target = getSafeReturnTo(defaultPath);
  // Heuristic: if it's an absolute URL (starts with `http`), full-reload to
  // it. Otherwise SPA-navigate.
  if (/^https?:\/\//i.test(target)) {
    window.location.href = target;
  } else {
    navigate(target);
  }
}

/**
 * Build a target path that preserves the current page's `return_to` query
 * param. Used by Login ↔ Register cross-links so that pivoting between
 * forms doesn't drop the deep-link the user came in with.
 *
 * Returns `to` unchanged when no `return_to` is present.
 */
export function withReturnTo(to: string): string {
  if (typeof window === 'undefined') return to;
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('return_to');
  if (!returnTo) return to;
  const sep = to.includes('?') ? '&' : '?';
  return `${to}${sep}return_to=${encodeURIComponent(returnTo)}`;
}
